import { useId, useState, type ComponentPropsWithRef, type ComponentType, type ReactNode } from 'react';
import { IconLoaderCircle, type IconProps } from '../icons';

/**
 * The two buttons the whole product is built from.
 *
 * <p>Adapted from the Norva design package. One `primary` per page or form, and labels start
 * with a verb, because "Ekle" on its own leaves the reader to work out what is being added.</p>
 */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'success' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ComponentPropsWithRef<'button'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  /** Shows a spinner, keeps the width and disables the button. Pass the "…ing" label too. */
  loading?: boolean;
  loadingText?: ReactNode;
  icon?: ComponentType<IconProps>;
  trailingIcon?: ComponentType<IconProps>;
};

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

export function Button({
  variant = 'secondary',
  size = 'md',
  block,
  loading = false,
  loadingText,
  icon: Icon,
  trailingIcon: TrailingIcon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconSize = size === 'lg' ? 18 : 16;

  return (
    <button
      type={type}
      className={cx('nv-btn', `nv-btn--${variant}`, size !== 'md' && `nv-btn--${size}`, block && 'nv-btn--block', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <IconLoaderCircle size={iconSize} className="nv-spin" /> : Icon ? <Icon size={iconSize} /> : null}
      {loading && loadingText ? loadingText : children}
      {!loading && TrailingIcon ? <TrailingIcon size={14} /> : null}
    </button>
  );
}

export type IconButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'aria-label'> & {
  /** Required: the accessible name *and* the visible tooltip. An icon on its own says nothing. */
  label: string;
  icon: ComponentType<IconProps>;
  tone?: 'default' | 'danger';
  iconSize?: number;
  /** Hidden while a menu this button opened is on screen. */
  tooltip?: boolean;
};

/**
 * An icon-only button that can be read.
 *
 * <p>The tooltip appears on hover <em>and</em> on keyboard focus. The previous screens had a
 * bare pencil glyph with no accessible name at all, which a screen reader announced as
 * "button".</p>
 */
export function IconButton({
  label,
  icon: Icon,
  tone = 'default',
  iconSize = 16,
  tooltip = true,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const [show, setShow] = useState(false);
  const tooltipId = useId();
  const { onMouseEnter, onMouseLeave, onFocus, onBlur } = rest;

  return (
    <button
      type={type}
      aria-label={label}
      aria-describedby={show && tooltip ? tooltipId : undefined}
      className={cx('nv-icon-btn', tone === 'danger' && 'nv-icon-btn--danger', className)}
      {...rest}
      onMouseEnter={(e) => { setShow(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setShow(false); onMouseLeave?.(e); }}
      onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) setShow(true); onFocus?.(e); }}
      onBlur={(e) => { setShow(false); onBlur?.(e); }}
    >
      <Icon size={iconSize} />
      {show && tooltip ? (
        <span className="nv-tooltip" role="tooltip" id={tooltipId}>
          {label}
        </span>
      ) : null}
    </button>
  );
}
